import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";
import SEO from "@/components/SEO";
import Marquee from "@/components/Marquee";
import { Reveal, Eyebrow, EASE } from "@/components/Reveal";
import { useBooking } from "@/components/booking";
import { BUSINESS, IMAGES, WA_DEFAULT } from "@/data/business";

const OFFERINGS = ["Bridal Hair", "Bridal Services", "Make-up Services"];

function EditorialImage({ img, className, caption, testid }) {
  return (
    <Reveal className={className}>
      <figure>
        <div className="arch overflow-hidden border border-champagne/25">
          <img src={img.src} alt={img.alt} loading="lazy" className="w-full object-cover transition-transform duration-[1.2s] hover:scale-[1.04]" />
        </div>
        {caption && (
          <figcaption className="mt-4 text-center text-[11px] font-semibold uppercase tracking-[0.24em] text-ivory/40">
            {caption}
          </figcaption>
        )}
      </figure>
    </Reveal>
  );
}

export default function Bridal() {
  const { openBooking } = useBooking();
  const reduce = useReducedMotion();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 70]);

  return (
    <main className="bg-ink text-ivory">
      <SEO
        title="Bridal & Makeup"
        description="Bridal hair, bridal services and make-up services at Glam Girl Makeup Studio, Patel Nagar / Hardattpur, Varanasi. Book a bridal consultation."
      />

      <section ref={heroRef} data-testid="bridal-hero" className="grain relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 pb-24 pt-32 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:pt-44">
          <div>
            <Reveal>
              <Eyebrow dark>The Bridal Room</Eyebrow>
            </Reveal>
            <h1 className="mt-5 font-serif text-4xl font-medium leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl">
              <span className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={reduce ? false : { y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.2, ease: EASE }}
                >
                  Bridal &amp; Makeup
                </motion.span>
              </span>
            </h1>
            <Reveal delay={0.35}>
              <p className="mt-6 text-sm font-semibold uppercase tracking-[0.22em] text-champagne">
                Bridal Hair · Bridal Services · Make-up Services
              </p>
              <p className="mt-5 max-w-md text-base leading-relaxed text-ivory/60">
                Your occasion, your look. We take each booking at an unhurried pace —
                styled in the studio, finished under our arch lights.
              </p>
            </Reveal>
            <Reveal delay={0.5}>
              <div className="mt-9 flex flex-wrap gap-4">
                <button
                  onClick={() => openBooking("Bridal Services")}
                  data-testid="bridal-book-button"
                  className="group inline-flex items-center gap-2 rounded-full bg-ivory px-8 py-4 text-sm font-semibold tracking-wide text-ink transition-colors hover:bg-champagne"
                >
                  Book Bridal Consultation
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <a
                  href={WA_DEFAULT}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="bridal-whatsapp-button"
                  className="inline-flex items-center gap-2 rounded-full border border-ivory/25 px-7 py-4 text-sm font-semibold tracking-wide text-ivory transition-colors hover:border-champagne hover:text-champagne"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp Now
                </a>
              </div>
            </Reveal>
          </div>

          <motion.div style={{ y }} className="relative mx-auto w-full max-w-sm">
            <Reveal delay={0.3}>
              <div className="arch overflow-hidden border border-champagne/30 shadow-[0_40px_100px_rgba(0,0,0,0.5)]">
                <img
                  src={IMAGES.bridalMirror.src}
                  alt={IMAGES.bridalMirror.alt}
                  className="aspect-[3/4] w-full object-cover"
                  fetchpriority="high"
                />
              </div>
            </Reveal>
            <div className="absolute -bottom-5 left-1/2 h-20 w-[80%] -translate-x-1/2 rounded-[50%] bg-black/60 blur-2xl" aria-hidden="true" />
          </motion.div>
        </div>
        <Marquee dark />
      </section>

      <section data-testid="bridal-editorial" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
        <Reveal className="max-w-xl">
          <Eyebrow dark>The Lookbook</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl">
            Recent looks from <em className="text-champagne">the studio.</em>
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          <EditorialImage img={IMAGES.bridalDoor} testid="bridal-img-door" caption="At the studio entrance" className="lg:mt-16" />
          <EditorialImage img={IMAGES.bridalPortrait} testid="bridal-img-portrait" caption="Bridal makeup, finished" />
          <EditorialImage img={IMAGES.bridalMirror} testid="bridal-img-mirror" caption="Under the arch lights" className="lg:mt-24" />
        </div>
      </section>

      <section data-testid="bridal-offerings" className="border-t border-ivory/10">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-28">
          <div className="grid gap-14 lg:grid-cols-2">
            <Reveal>
              <Eyebrow dark>What's Included</Eyebrow>
              <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight sm:text-4xl">
                Your occasion, your look.
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-ivory/60">
                Our bridal and occasion work covers the essentials done properly —
                hair and makeup, tailored to your outfit, your jewellery and your day.
                Enquire and we'll plan the details together.
              </p>
            </Reveal>
            <div>
              {OFFERINGS.map((o, i) => (
                <Reveal key={o} delay={0.07 * i}>
                  <div
                    className="flex items-baseline justify-between border-b border-ivory/10 py-6"
                    data-testid={`bridal-offering-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    <span className="font-serif text-2xl font-medium sm:text-3xl">{o}</span>
                    <span className="font-serif text-lg italic text-champagne">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.25}>
                <div className="mt-9 flex flex-wrap items-center gap-5">
                  <button
                    onClick={() => openBooking("Bridal Services")}
                    data-testid="bridal-offerings-book-button"
                    className="rounded-full bg-ivory px-8 py-4 text-sm font-semibold tracking-wide text-ink transition-colors hover:bg-champagne"
                  >
                    Book Bridal Consultation
                  </button>
                  <p className="text-xs text-ivory/40">{BUSINESS.hoursLine}</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
